import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Upload, FileText, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  listMyDocuments,
  recordClientDocument,
} from "@/lib/api/documents.functions";
import { getMyProfile } from "@/lib/api/portal.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NewFilesNotice } from "@/components/portal/NewFilesNotice";

export const Route = createFileRoute("/_authenticated/portal/documents")({
  head: () => ({
    meta: [
      { title: "Upload documents | Client portal" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DocumentsPage,
});

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB
const ACCEPTED =
  ".pdf,.doc,.docx,.txt,.rtf,.odt,.png,.jpg,.jpeg,.webp,.heic,.xls,.xlsx,.csv";

function bytesPretty(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function DocumentsPage() {
  const qc = useQueryClient();
  const fetchProfile = useServerFn(getMyProfile);
  const fetchDocs = useServerFn(listMyDocuments);
  const saveMeta = useServerFn(recordClientDocument);

  const { data: prof } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
  });
  const { data: docsData } = useQuery({
    queryKey: ["my-documents"],
    queryFn: () => fetchDocs(),
  });

  const [matter, setMatter] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a file to upload.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File too large", { description: "Max 25 MB per file." });
      return;
    }
    const { data: userRes } = await supabase.auth.getUser();
    const user = userRes.user;
    if (!user) {
      toast.error("Please sign in again.");
      return;
    }
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `${user.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("client-documents")
        .upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type || undefined,
        });
      if (upErr) throw upErr;

      await saveMeta({
        data: {
          uploader_name: prof?.profile?.full_name || "Client",
          uploader_email: prof?.profile?.email || user.email || "",
          uploader_phone: prof?.profile?.phone || "",
          matter_summary: matter,
          storage_path: path,
          original_filename: file.name,
          size_bytes: file.size,
          mime_type: file.type || "",
        },
      });

      toast.success("Document uploaded securely", {
        description: "Only you and the admin can see it.",
      });
      setFile(null);
      setMatter("");
      (document.getElementById("doc-file") as HTMLInputElement | null)?.value &&
        ((document.getElementById("doc-file") as HTMLInputElement).value = "");
      qc.invalidateQueries({ queryKey: ["my-documents"] });
    } catch (err: any) {
      toast.error("Upload failed", { description: err?.message ?? String(err) });
    } finally {
      setUploading(false);
    }
  }

  const docs = docsData?.documents ?? [];

  return (
    <div className="space-y-8">
      <NewFilesNotice />
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Confidential uploads</p>
        <h2 className="mt-1 font-display text-2xl text-navy">Send us your documents</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Files are stored in a private vault. Only you and Nkadimeng Danny can
          access them. POPIA-aligned handling.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4"
      >
        <div className="flex items-start gap-3 rounded-lg border border-gold/40 bg-secondary/30 p-3 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 text-gold shrink-0" />
          <p>
            Up to 25 MB per file. Accepted: PDF, Word, images, spreadsheets,
            text. Files are encrypted at rest.
          </p>
        </div>

        <div>
          <Label htmlFor="matter">Brief matter description (optional)</Label>
          <Textarea
            id="matter"
            value={matter}
            onChange={(e) => setMatter(e.target.value)}
            maxLength={2000}
            placeholder="E.g. CCMA referral form and supporting emails for unfair dismissal claim."
            className="mt-1 min-h-[88px]"
          />
        </div>

        <div>
          <Label htmlFor="doc-file">Document</Label>
          <Input
            id="doc-file"
            type="file"
            accept={ACCEPTED}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1"
            required
          />
          {file && (
            <p className="mt-1 text-xs text-muted-foreground">
              {file.name} · {bytesPretty(file.size)}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={uploading || !file}
          className="bg-navy hover:bg-navy/90"
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" /> Upload securely
            </>
          )}
        </Button>
      </form>




      <section>
        <h3 className="font-display text-lg text-navy">Your uploads</h3>
        {docs.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            No documents yet. Files you upload will appear here.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-border rounded-xl border border-border bg-card">
            {docs.map((d: any) => (
              <li key={d.id} className="flex items-start gap-3 p-4">
                <FileText className="mt-0.5 h-4 w-4 text-navy shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {d.case_reference && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-mono text-navy">
                        {d.case_reference}
                      </span>
                    )}
                    {d.payment_status && (
                      <span
                        className={
                          "rounded-full border px-2 py-0.5 text-[11px] font-medium " +
                          (d.payment_status === "paid"
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : d.payment_status === "partial"
                              ? "bg-amber-100 text-amber-700 border-amber-200"
                              : d.payment_status === "waived"
                                ? "bg-slate-100 text-slate-700 border-slate-200"
                                : "bg-rose-100 text-rose-700 border-rose-200")
                        }
                      >
                        {d.payment_status === "paid"
                          ? "Paid"
                          : d.payment_status === "partial"
                            ? `Partial · R${((d.amount_paid_cents ?? 0) / 100).toFixed(2)} / R${((d.amount_due_cents ?? 0) / 100).toFixed(2)}`
                            : d.payment_status === "waived"
                              ? "Waived"
                              : d.amount_due_cents
                                ? `Unpaid · R${(d.amount_due_cents / 100).toFixed(2)}`
                                : "Unpaid"}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm font-medium text-navy">{d.original_filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {bytesPretty(d.size_bytes)} ·{" "}
                    {new Date(d.created_at).toLocaleString("en-ZA")}
                  </p>
                  {d.matter_summary && (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {d.matter_summary}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
